import { useCallback, useEffect, useState } from 'react';
import {
    AlertTriangle, Check, ChevronDown, ChevronRight, ExternalLink, Info, Loader2,
    Lock, Plus, RefreshCw, Save,
} from 'lucide-react';
import { visitReportsApi } from '../../services/visitReportsApi';
import type {
    MarketdataMatchCandidate,
    MarketdataPlanEntry,
    MarketdataPlanResponse,
    MarketdataWriteResponse,
} from '../../types/visitReport';

/**
 * The market-data step: what the visit learned, becoming records in the live CRM.
 *
 * It appears **after** the report has synced and is a step of its own, not part of the sync
 * (D4). The sync is already not atomic; a third write path inside the same button press would
 * multiply its failure cases and let a rejected market data record throw away a synced report.
 *
 * ── THREE THINGS THIS SCREEN IS BUILT AROUND ───────────────────────────────────────────────
 *
 * 1. **Every value is shown before it is sent, and one card is acknowledged at a time.** The
 *    property list is what the *server* would send, not a re-derivation in the browser — the
 *    same promise the Market Intelligence picker makes about the alternatives string. For an
 *    update the live value is shown beside the new one, so "what will change" is readable
 *    rather than inferred.
 *
 * 2. **A match is offered, never assumed.** The server produces a verdict only when the product
 *    name AND the vendor agree with exactly one active record. Everything short of that — a
 *    different vendor, a vendor on one side only, a different spelling, a deactivated record —
 *    is shown as a candidate the rep can pick, with the reason written out. `choose` means the
 *    CRM already holds duplicates and nothing is defaulted.
 *
 * 3. **A failure on one card leaves every other card and the report alone**, and the step can
 *    be run again. The guarantee against a double create is on the server (its own audit log,
 *    keyed on the report), not in this component's state — a rep reloads, switches device and
 *    closes tabs.
 */

interface Props {
    visitReportId: string;
    companyName?: string | null;
}

type CardState =
    | { status: 'idle' }
    | { status: 'writing' }
    | { status: 'done'; result: MarketdataWriteResponse }
    | { status: 'error'; message: string };

function recordUrl(portalId: string, objectTypeId: string | undefined, id: string): string | null {
    if (!objectTypeId) return null;
    return `https://app.hubspot.com/contacts/${portalId}/record/${objectTypeId}/${id}`;
}

/** HubSpot property name → what a rep calls it. Unknown names fall through unchanged. */
const FIELD_LABELS: Record<string, string> = {
    name: 'Product',
    vendor: 'Vendor',
    item_category: 'Item category',
    currency_code: 'Currency',
    volume_year: 'Volume / year (kg)',
    price_kg: 'Price / kg',
    dolder_alternative_product: 'Dolder alternative',
    dolder_company: 'Dolder entity',
    note: 'Note',
    active: 'Active',
};

export function MarketDataStep({ visitReportId, companyName }: Props) {
    const [plan, setPlan] = useState<MarketdataPlanResponse | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [cards, setCards] = useState<Record<number, CardState>>({});
    /** Per card: the chosen action and, for an update, the record it targets. */
    const [choice, setChoice] = useState<Record<number, { action: 'create' | 'update'; marketdataId?: string }>>({});
    const [expanded, setExpanded] = useState<Record<number, boolean>>({});

    const load = useCallback(async () => {
        setIsLoading(true);
        setLoadError(null);
        try {
            const result = await visitReportsApi.marketDataPlan(visitReportId);
            setPlan(result);
            // The server's default is the starting point, not a decision: `choose` leaves the
            // card without one until the rep picks, and a blocked card gets none at all.
            const next: Record<number, { action: 'create' | 'update'; marketdataId?: string }> = {};
            for (const entry of result.entries) {
                if (entry.defaultAction === 'update' && entry.match.target) {
                    next[entry.index] = { action: 'update', marketdataId: entry.match.target.marketdataId };
                } else if (entry.defaultAction === 'create') {
                    next[entry.index] = { action: 'create' };
                }
            }
            setChoice(next);
        } catch (err) {
            setLoadError(err instanceof Error ? err.message : String(err));
            setPlan(null);
        } finally {
            setIsLoading(false);
        }
    }, [visitReportId]);

    useEffect(() => { void load(); }, [load]);

    const write = async (entry: MarketdataPlanEntry) => {
        const picked = choice[entry.index];
        if (!picked) return;
        setCards(c => ({ ...c, [entry.index]: { status: 'writing' } }));
        try {
            const result = await visitReportsApi.writeMarketData(visitReportId, {
                observationIndex: entry.index,
                // The name travels with the index: an index is a position, and a position is
                // not an identity. The server answers 409 if the two disagree.
                competitorProduct: entry.competitorProduct,
                action: picked.action,
                marketdataId: picked.marketdataId,
            });
            setCards(c => ({ ...c, [entry.index]: { status: 'done', result } }));
        } catch (err) {
            setCards(c => ({
                ...c,
                [entry.index]: { status: 'error', message: err instanceof Error ? err.message : String(err) },
            }));
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                Checking what this visit would add to the market data…
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="space-y-2">
                        <p className="font-medium">Market data could not be prepared.</p>
                        <p>{loadError}</p>
                        <p className="text-amber-800">
                            The report itself is synced and unaffected. Nothing was written.
                        </p>
                        <button
                            type="button"
                            onClick={() => void load()}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-amber-300 rounded-lg bg-white hover:bg-amber-100"
                        >
                            <RefreshCw className="w-3.5 h-3.5" /> Try again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!plan) return null;

    if (plan.blockedReason) {
        return (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                <div className="flex items-start gap-2">
                    <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <p>{plan.blockedReason}</p>
                </div>
            </div>
        );
    }

    if (plan.observationCount === 0) return null;

    const outstanding = plan.entries.filter(
        e => cards[e.index]?.status !== 'done' && !e.alreadyWritten && e.defaultAction !== 'blocked',
    );
    const createsLeft = outstanding.filter(e => choice[e.index]?.action === 'create').length;
    const updatesLeft = outstanding.filter(e => choice[e.index]?.action === 'update').length;

    return (
        <div className="space-y-3 text-left w-full">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h4 className="text-base font-semibold text-gray-800">
                        Market data{companyName ? ` for ${companyName}` : ''}
                    </h4>
                    <p className="text-sm text-gray-600">
                        {createsLeft + updatesLeft === 0
                            ? 'Nothing left to write.'
                            : `Create ${createsLeft} market data ${createsLeft === 1 ? 'record' : 'records'}, `
                              + `update ${updatesLeft}.`}
                        {' '}Each one is confirmed on its own; the report is already in HubSpot either way.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => void load()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                >
                    <RefreshCw className="w-3.5 h-3.5" /> Refresh
                </button>
            </div>

            {plan.entries.map(entry => (
                <EntryCard
                    key={entry.index}
                    entry={entry}
                    state={cards[entry.index] ?? { status: 'idle' }}
                    choice={choice[entry.index]}
                    portalId={plan.portalId}
                    objectTypeId={plan.objectTypeId}
                    isExpanded={expanded[entry.index] ?? true}
                    onToggle={() => setExpanded(e => ({ ...e, [entry.index]: !(e[entry.index] ?? true) }))}
                    onChoose={next => setChoice(c => ({ ...c, [entry.index]: next }))}
                    onWrite={() => void write(entry)}
                />
            ))}
        </div>
    );
}

// -----------------------------------------------------------------------------
// One observation
// -----------------------------------------------------------------------------

function EntryCard({
    entry, state, choice, portalId, objectTypeId, isExpanded, onToggle, onChoose, onWrite,
}: {
    entry: MarketdataPlanEntry;
    state: CardState;
    choice?: { action: 'create' | 'update'; marketdataId?: string };
    portalId: string;
    objectTypeId?: string;
    isExpanded: boolean;
    onToggle: () => void;
    onChoose: (next: { action: 'create' | 'update'; marketdataId?: string }) => void;
    onWrite: () => void;
}) {
    const isBlocked = entry.blockers.length > 0;
    const done = state.status === 'done' ? state.result : null;
    // Written by an earlier run of this step. The server refuses a second create regardless;
    // this is so the rep is not left wondering why the button is gone.
    const previously = entry.alreadyWritten;

    const isUpdate = choice?.action === 'update';
    const properties = isUpdate ? (entry.updateProperties ?? {}) : entry.createProperties;
    const before = isUpdate ? entry.updateBefore : null;
    const targetForUpdate = isUpdate
        ? entry.match.candidates.find(c => c.marketdataId === choice?.marketdataId) ?? entry.match.target
        : null;

    return (
        <div className={`rounded-lg border ${done ? 'border-green-200 bg-green-50' : isBlocked ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'}`}>
            <button
                type="button"
                onClick={onToggle}
                className="w-full flex items-center gap-2 px-3 py-2.5 text-left"
            >
                {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <span className="font-medium text-gray-800 truncate">{entry.competitorProduct}</span>
                <span className="ml-auto flex-shrink-0">
                    {done ? (
                        <Badge tone="green">
                            {done.operation === 'create-from-visit' ? 'Created' : 'Updated'} {done.marketdataId}
                        </Badge>
                    ) : previously ? (
                        <Badge tone="green">Already written · {previously.marketdataId}</Badge>
                    ) : isBlocked ? (
                        <Badge tone="red">Cannot be written</Badge>
                    ) : entry.defaultAction === 'choose' ? (
                        <Badge tone="amber">Pick one</Badge>
                    ) : choice?.action === 'update' ? (
                        <Badge tone="blue">Update</Badge>
                    ) : (
                        <Badge tone="gray">Create</Badge>
                    )}
                </span>
            </button>

            {isExpanded && (
                <div className="px-3 pb-3 space-y-3">
                    {entry.blockers.map((b, i) => (
                        <Callout key={i} tone="red" icon={<AlertTriangle className="w-4 h-4" />}>{b}</Callout>
                    ))}
                    {entry.warnings.map((w, i) => (
                        <Callout key={i} tone="amber" icon={<Info className="w-4 h-4" />}>{w}</Callout>
                    ))}

                    {previously && !done && (
                        <Callout tone="green" icon={<Check className="w-4 h-4" />}>
                            This report already wrote record{' '}
                            <RecordLink portalId={portalId} objectTypeId={objectTypeId} id={previously.marketdataId} />
                            {' '}on {new Date(previously.at).toLocaleString('de-CH')}. Running the step again
                            will not create a second one.
                        </Callout>
                    )}

                    {/* What would be sent. For an update, beside what is there now. */}
                    {!done && !isBlocked && (
                        <PropertyTable properties={properties} before={before} />
                    )}

                    {!done && !isBlocked && entry.alternativeValue && (
                        <div className="text-sm">
                            <div className="text-gray-500 mb-1">
                                Exactly this goes into <code>dolder_alternative_product</code>
                                {entry.alternativeLinkCount > 0 && (
                                    <> — and {entry.alternativeLinkCount} link{entry.alternativeLinkCount === 1 ? '' : 's'} into our own product master</>
                                )}
                                :
                            </div>
                            <code className="block px-2 py-1.5 bg-gray-100 rounded text-gray-800 break-all">
                                {entry.alternativeValue}
                            </code>
                        </div>
                    )}

                    {!done && !isBlocked && isUpdate && entry.reactivates && (
                        <Callout tone="amber" icon={<AlertTriangle className="w-4 h-4" />}>
                            That record is currently <strong>deactivated</strong>. Updating it will make it
                            visible again in Market Intelligence.
                        </Callout>
                    )}

                    {/* The match, and the choice it leaves open. */}
                    {!done && !isBlocked && (
                        <MatchChooser
                            entry={entry}
                            choice={choice}
                            portalId={portalId}
                            objectTypeId={objectTypeId}
                            onChoose={onChoose}
                        />
                    )}

                    {state.status === 'error' && (
                        <Callout tone="red" icon={<AlertTriangle className="w-4 h-4" />}>
                            <span className="font-medium">Nothing was written for this record.</span>{' '}
                            {state.message}{' '}
                            <span className="text-red-800">
                                The report and every other record are unaffected — you can try this one again.
                            </span>
                        </Callout>
                    )}

                    {done && (
                        <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2 text-green-800">
                                <Check className="w-4 h-4" />
                                <span>
                                    {done.operation === 'create-from-visit' ? 'Created' : 'Updated'} record{' '}
                                    <RecordLink portalId={portalId} objectTypeId={done.objectTypeId} id={done.marketdataId} />
                                    {done.changedFields.length > 0 && <> · {done.changedFields.length} field{done.changedFields.length === 1 ? '' : 's'}</>}
                                    {done.links.length > 0 && <> · {done.links.length} link row{done.links.length === 1 ? '' : 's'}</>}
                                </span>
                            </div>
                            {done.reactivated && (
                                <Callout tone="amber" icon={<Info className="w-4 h-4" />}>
                                    The record was deactivated and is now active again.
                                </Callout>
                            )}
                            {!done.linksWritten && (
                                <Callout tone="amber" icon={<AlertTriangle className="w-4 h-4" />}>
                                    The text is in HubSpot but the link rows were not written, so Market
                                    Intelligence will show this alternative as <em>not linked</em>. That is the
                                    honest state and it can be fixed in the tab. {done.linkError}
                                </Callout>
                            )}
                            {/* Read straight back out of HubSpot after the write, not echoed from
                                the request — two defects in the first live marketdata write
                                survived strict typing and showed up only on a real record. */}
                            {done.item && <ReadBack item={done.item} />}
                        </div>
                    )}

                    {!done && (
                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={onWrite}
                                disabled={isBlocked || !choice || state.status === 'writing'}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-300"
                            >
                                {state.status === 'writing'
                                    ? <Loader2 className="w-4 h-4 animate-spin" />
                                    : choice?.action === 'update' ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                                {choice?.action === 'update'
                                    ? `Update ${targetForUpdate?.marketdataId ?? 'record'}`
                                    : 'Create record'}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * Create, or update which record.
 *
 * Only an `exact` verdict arrives pre-selected. Everything else lists what the server found and
 * why it did not act on it — the reason is the point: "same name, different vendor" is
 * something a rep can judge in a second and a machine cannot judge at all.
 */
function MatchChooser({
    entry, choice, portalId, objectTypeId, onChoose,
}: {
    entry: MarketdataPlanEntry;
    choice?: { action: 'create' | 'update'; marketdataId?: string };
    portalId: string;
    objectTypeId?: string;
    onChoose: (next: { action: 'create' | 'update'; marketdataId?: string }) => void;
}) {
    const name = `action-${entry.index}`;
    const hasCandidates = entry.match.candidates.length > 0;

    return (
        <div className="space-y-1.5 text-sm border-t border-gray-100 pt-2">
            {entry.match.verdict === 'ambiguous' && (
                <Callout tone="amber" icon={<AlertTriangle className="w-4 h-4" />}>
                    The CRM already holds more than one record with this name and vendor. Nothing is
                    preselected — pick the one you mean, or create another.
                </Callout>
            )}

            <label className="flex items-start gap-2 cursor-pointer">
                <input
                    type="radio"
                    name={name}
                    className="mt-1"
                    checked={choice?.action === 'create'}
                    onChange={() => onChoose({ action: 'create' })}
                />
                <span className="text-gray-700">
                    Create a new market data record
                    {!hasCandidates && <span className="text-gray-400"> — nothing similar exists on this customer</span>}
                </span>
            </label>

            {entry.match.candidates.map(candidate => (
                <label key={candidate.marketdataId} className="flex items-start gap-2 cursor-pointer">
                    <input
                        type="radio"
                        name={name}
                        className="mt-1"
                        checked={choice?.action === 'update' && choice.marketdataId === candidate.marketdataId}
                        onChange={() => onChoose({ action: 'update', marketdataId: candidate.marketdataId })}
                    />
                    <span className="text-gray-700">
                        Update <RecordLink portalId={portalId} objectTypeId={objectTypeId} id={candidate.marketdataId} />
                        {' — '}
                        <CandidateSummary candidate={candidate} />
                        <span className="block text-xs text-gray-500">{candidate.reason}</span>
                    </span>
                </label>
            ))}
        </div>
    );
}

function CandidateSummary({ candidate }: { candidate: MarketdataMatchCandidate }) {
    const bits = [
        candidate.name,
        candidate.vendor || 'no vendor',
        candidate.volumeYear ? `${candidate.volumeYear} kg/y` : null,
        candidate.priceKg ? `${candidate.priceKg}/kg` : null,
        candidate.active ? null : 'inactive',
    ].filter(Boolean);
    return <span className="text-gray-600">{bits.join(' · ')}</span>;
}

/** Every value that would be sent, and for an update the value it replaces. */
function PropertyTable({
    properties, before,
}: {
    properties: Record<string, string>;
    before: Record<string, string | null> | null;
}) {
    const keys = Object.keys(properties);
    if (keys.length === 0) {
        return <p className="text-sm text-gray-500">Nothing on that record would change.</p>;
    }
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <tbody>
                    {keys.map(key => (
                        <tr key={key} className="border-b border-gray-100 last:border-0 align-top">
                            <td className="py-1 pr-3 text-gray-500 whitespace-nowrap w-40">
                                {FIELD_LABELS[key] ?? key}
                            </td>
                            {before && (
                                <td className="py-1 pr-3 text-gray-400 line-through whitespace-pre-wrap break-words max-w-xs">
                                    {before[key] ?? '—'}
                                </td>
                            )}
                            <td className="py-1 text-gray-800 whitespace-pre-wrap break-words">
                                {properties[key] || '—'}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/** The record as HubSpot hands it back, field by field. */
function ReadBack({ item }: { item: Record<string, unknown> }) {
    const shown: Array<[string, unknown]> = Object.entries(item)
        .filter(([, v]) => v !== null && v !== undefined && v !== '');
    if (shown.length === 0) return null;
    return (
        <details className="text-xs text-gray-600">
            <summary className="cursor-pointer text-gray-500">Read back from HubSpot</summary>
            <table className="mt-1 w-full">
                <tbody>
                    {shown.map(([k, v]) => (
                        <tr key={k} className="align-top">
                            <td className="pr-3 text-gray-400 whitespace-nowrap">{k}</td>
                            <td className="whitespace-pre-wrap break-words">{String(v)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </details>
    );
}

function RecordLink({ portalId, objectTypeId, id }: { portalId: string; objectTypeId?: string; id: string }) {
    const url = recordUrl(portalId, objectTypeId, id);
    if (!url) return <span className="font-mono">{id}</span>;
    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-blue-600 hover:underline inline-flex items-center gap-1"
        >
            {id}<ExternalLink className="w-3 h-3" />
        </a>
    );
}

function Badge({ tone, children }: { tone: 'green' | 'red' | 'amber' | 'blue' | 'gray'; children: React.ReactNode }) {
    const tones = {
        green: 'bg-green-100 text-green-800',
        red: 'bg-red-100 text-red-800',
        amber: 'bg-amber-100 text-amber-900',
        blue: 'bg-blue-100 text-blue-800',
        gray: 'bg-gray-100 text-gray-700',
    } as const;
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tones[tone]}`}>{children}</span>;
}

function Callout({
    tone, icon, children,
}: {
    tone: 'green' | 'red' | 'amber';
    icon: React.ReactNode;
    children: React.ReactNode;
}) {
    const tones = {
        green: 'border-green-200 bg-green-50 text-green-900',
        red: 'border-red-200 bg-red-50 text-red-900',
        amber: 'border-amber-200 bg-amber-50 text-amber-900',
    } as const;
    return (
        <div className={`flex items-start gap-2 rounded-lg border px-2.5 py-2 text-sm ${tones[tone]}`}>
            <span className="mt-0.5 flex-shrink-0">{icon}</span>
            <div>{children}</div>
        </div>
    );
}
